import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { MdClose, MdContentCopy, MdGroupAdd, MdRefresh } from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import { getVideoInvite, updateVideoInvite, type VideoInvite, type VideoRoom } from '@/lib/api/video/video-api'

/** `datetime-local` trabalha no horário local, sem fuso: converte nos dois sentidos. */
function toLocalInput(iso: string | null): string {
    if (!iso) return ''
    const date = new Date(iso)
    const offset = date.getTimezoneOffset() * 60_000
    return new Date(date.getTime() - offset).toISOString().slice(0, 16)
}

function fromLocalInput(value: string): string | null {
    return value ? new Date(value).toISOString() : null
}

/**
 * Link de convite da sala: um endereço só para a reunião, em vez de um link por
 * pessoa. Quem abre digita o nome e entra como participante; com a sala de
 * espera ligada, aguarda o moderador admitir.
 */
export default function VideoInviteModal({
    room,
    editable,
    onClose,
}: {
    room: VideoRoom
    editable: boolean
    onClose: () => void
}) {
    const [invite, setInvite] = useState<VideoInvite | null>(null)
    const [expiresAt, setExpiresAt] = useState('')
    const [saving, setSaving] = useState(false)
    const [confirmRegenerate, setConfirmRegenerate] = useState(false)
    // O pai passa uma função nova a cada render: a ref evita recarregar o convite por isso.
    const onCloseRef = useRef(onClose)
    onCloseRef.current = onClose

    useEffect(() => {
        void getVideoInvite(room.id).then(response => {
            if (response.success && response.result) {
                setInvite(response.result)
                setExpiresAt(toLocalInput(response.result.expiresAt))
            } else {
                toast.error(response.error || 'Não foi possível carregar o convite')
                onCloseRef.current()
            }
        })
    }, [room.id])

    const save = async (data: Parameters<typeof updateVideoInvite>[1], success?: string) => {
        setSaving(true)
        const response = await updateVideoInvite(room.id, data)
        setSaving(false)
        if (!response.success || !response.result) {
            toast.error(response.error || 'Não foi possível salvar o convite')
            return false
        }
        setInvite(response.result)
        setExpiresAt(toLocalInput(response.result.expiresAt))
        if (success) toast.success(success)
        return true
    }

    const handleCopy = async () => {
        if (!invite?.url) return
        try {
            await navigator.clipboard.writeText(invite.url)
            toast.success('Link copiado')
        } catch {
            toast.error('Não foi possível copiar')
        }
    }

    const expiryChanged = invite ? toLocalInput(invite.expiresAt) !== expiresAt : false

    return (
        <div className="modal modal-open">
            <div className="modal-box max-w-lg">
                <div className="flex items-center justify-between mb-1">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                        <MdGroupAdd className="text-xl text-primary" />
                        Link de convite
                    </h3>
                    <button type="button" className="btn btn-sm btn-ghost btn-circle" onClick={onClose}>
                        <MdClose className="text-xl" />
                    </button>
                </div>
                <p className="text-sm text-base-content/70 mb-5">
                    Um link só para toda a reunião. Quem abre informa o nome e entra como participante.
                </p>

                {!invite ? (
                    <div className="flex justify-center py-8">
                        <span className="loading loading-spinner loading-md text-primary" />
                    </div>
                ) : (
                    <div className="space-y-4">
                        <label className="label cursor-pointer justify-start items-start gap-3 p-0 whitespace-normal">
                            <input
                                type="checkbox"
                                className="toggle toggle-primary mt-0.5"
                                checked={invite.enabled}
                                disabled={!editable || saving}
                                onChange={event =>
                                    void save(
                                        { enabled: event.target.checked },
                                        event.target.checked ? 'Convite ligado' : 'Convite desligado',
                                    )
                                }
                            />
                            <span>
                                <span className="label-text font-medium">Convite ligado</span>
                                <span className="block text-xs text-base-content/60 mt-0.5">
                                    Desligado, o link deixa de funcionar na hora.
                                </span>
                            </span>
                        </label>

                        <label className="label cursor-pointer justify-start items-start gap-3 p-0 whitespace-normal">
                            <input
                                type="checkbox"
                                className="toggle toggle-primary mt-0.5"
                                checked={invite.requiresApproval}
                                disabled={!editable || saving}
                                onChange={event => void save({ requiresApproval: event.target.checked })}
                            />
                            <span>
                                <span className="label-text font-medium">Sala de espera</span>
                                <span className="block text-xs text-base-content/60 mt-0.5">
                                    O convidado aguarda e um moderador admite ou recusa a entrada. Desligada, qualquer
                                    pessoa com o link entra direto.
                                </span>
                            </span>
                        </label>

                        <div className="form-control w-full">
                            <label className="label pb-1.5" htmlFor="video-invite-expires">
                                <span className="flex w-full items-baseline justify-between gap-3">
                                    <span className="label-text font-medium">Validade</span>
                                    <span className="label-text-alt text-base-content/50">opcional</span>
                                </span>
                            </label>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <input
                                    id="video-invite-expires"
                                    type="datetime-local"
                                    className="input input-bordered w-full"
                                    value={expiresAt}
                                    disabled={!editable || saving}
                                    onChange={event => setExpiresAt(event.target.value)}
                                />
                                {editable && expiryChanged && (
                                    <button
                                        type="button"
                                        className="btn btn-outline"
                                        disabled={saving}
                                        onClick={() => void save({ expiresAt: fromLocalInput(expiresAt) }, 'Validade salva')}
                                    >
                                        Salvar validade
                                    </button>
                                )}
                            </div>
                        </div>

                        {invite.enabled && invite.url && (
                            <div className="form-control w-full">
                                <label className="label pb-1.5" htmlFor="video-invite-link">
                                    <span className="flex w-full items-baseline justify-between gap-3">
                                        <span className="label-text font-medium">Link da reunião</span>
                                    </span>
                                </label>
                                <div className="join w-full">
                                    <input
                                        id="video-invite-link"
                                        type="text"
                                        className="input input-bordered join-item w-full font-mono text-xs"
                                        value={invite.url}
                                        readOnly
                                    />
                                    <button type="button" className="btn btn-primary join-item" onClick={handleCopy}>
                                        <MdContentCopy />
                                        Copiar
                                    </button>
                                </div>
                                {editable && (
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm self-start mt-2"
                                        disabled={saving}
                                        onClick={() => setConfirmRegenerate(true)}
                                    >
                                        <MdRefresh />
                                        Gerar link novo
                                    </button>
                                )}
                            </div>
                        )}

                        <div className="alert alert-info py-2">
                            <span className="text-sm">
                                O moderador continua entrando pelo link pessoal (botão de link na lista de salas). É ele
                                quem vê e admite quem está na sala de espera.
                            </span>
                        </div>
                    </div>
                )}

                <div className="modal-action">
                    <button type="button" className="btn btn-ghost" onClick={onClose}>
                        Fechar
                    </button>
                </div>
            </div>
            <div className="modal-backdrop" onClick={onClose} />

            <ConfirmModal
                isOpen={confirmRegenerate}
                onClose={() => setConfirmRegenerate(false)}
                onConfirm={async () => {
                    const ok = await save({ regenerate: true }, 'Link novo gerado')
                    if (ok) setConfirmRegenerate(false)
                }}
                loading={saving}
                title="Gerar link novo"
                message="O link atual deixa de funcionar. Quem já recebeu precisará do link novo."
                confirmButtonText="Gerar link novo"
            />
        </div>
    )
}
